/**
 * Target Registry for Poker Reborn Effects Engine
 *
 * Maps abstract effect targets (seat:3, pot, etc.) to screen positions.
 * Components register their positions here so effects can resolve targets.
 *
 * @see tasks/active/CHIPS_SYSTEM_IMPLEMENTATION_PLAN.md - Phase 5
 */

import { Container } from 'pixi.js';
import { log } from '@/utils/log';
import type { CommunityCardsView } from '@/components/table/CommunityCardsView';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Position with optional container reference.
 */
export interface TargetPosition {
  x: number;
  y: number;
  container?: Container | undefined;
}

/**
 * Registered seat data.
 */
export interface SeatRegistration {
  /** Seat chip animation anchor (betChipsContainer inside PlayerInfoBar) */
  position: TargetPosition;
  /** Player pot position (where bet chips go) */
  potPosition: TargetPosition;
  /** Avatar center position (for gift targeting) */
  avatarPosition?: TargetPosition | undefined;
  /** Hole cards position */
  cardsPosition?: TargetPosition | undefined;
}

// =============================================================================
// TARGET REGISTRY CLASS
// =============================================================================

/**
 * Registry for resolving effect targets to screen positions.
 *
 * TableScreen registers its views here during initialization.
 * Effect creators query this to get positions for animations.
 *
 * This is a singleton - use TargetRegistry.getInstance()
 */
export class TargetRegistry {
  private static _instance: TargetRegistry | null = null;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Seat registrations by seat number */
  private _seats: Map<number, SeatRegistration> = new Map();

  /** Table pot position */
  private _tablePot: TargetPosition | null = null;

  /** Particle layer for flying chips */
  private _particleLayer: Container | null = null;

  /** Table center position */
  private _tableCenter: TargetPosition | null = null;

  /** Community cards view for flip animations */
  private _communityCardsView: CommunityCardsView | null = null;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): TargetRegistry {
    if (!TargetRegistry._instance) {
      TargetRegistry._instance = new TargetRegistry();
    }
    return TargetRegistry._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    TargetRegistry._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Registration
  // ---------------------------------------------------------------------------

  /**
   * Register a seat's positions.
   */
  registerSeat(seat: number, registration: SeatRegistration): void {
    this._seats.set(seat, registration);
    log.effects.debug`TargetRegistry: registered seat ${seat}`;
  }

  /**
   * Update a seat's positions (e.g., after layout change).
   */
  updateSeat(seat: number, updates: Partial<SeatRegistration>): void {
    const current = this._seats.get(seat);
    if (current) {
      this._seats.set(seat, { ...current, ...updates });
    }
  }

  /**
   * Unregister a seat.
   */
  unregisterSeat(seat: number): void {
    this._seats.delete(seat);
  }

  /**
   * Register the table pot position.
   */
  registerTablePot(position: TargetPosition): void {
    this._tablePot = position;
    log.effects.debug`TargetRegistry: registered table pot`;
  }

  /**
   * Register the particle layer for flying effects.
   */
  registerParticleLayer(container: Container): void {
    this._particleLayer = container;
    log.effects.debug`TargetRegistry: registered particle layer`;
  }

  /**
   * Register the table center position.
   */
  registerTableCenter(position: TargetPosition): void {
    this._tableCenter = position;
  }

  /**
   * Register the community cards view for flip animations.
   */
  registerCommunityCards(view: CommunityCardsView): void {
    this._communityCardsView = view;
    log.effects.debug`TargetRegistry: registered community cards view`;
  }

  /**
   * Clear all registrations.
   */
  clear(): void {
    this._seats.clear();
    this._tablePot = null;
    this._particleLayer = null;
    this._tableCenter = null;
    this._communityCardsView = null;
  }

  // ---------------------------------------------------------------------------
  // Resolution
  // ---------------------------------------------------------------------------

  /**
   * Get a seat registration.
   */
  getSeat(seat: number): SeatRegistration | undefined {
    return this._seats.get(seat);
  }

  /**
   * Get seat position (betChipsContainer inside PlayerInfoBar).
   */
  getSeatPosition(seat: number): TargetPosition | undefined {
    return this._seats.get(seat)?.position;
  }

  /**
   * Get avatar center position for a seat (for gift targeting).
   * Falls back to seat position if avatar position not registered.
   */
  getAvatarPosition(seat: number): TargetPosition | undefined {
    const reg = this._seats.get(seat);
    return reg?.avatarPosition ?? reg?.position;
  }

  /**
   * Get player pot position for a seat.
   */
  getPlayerPotPosition(seat: number): TargetPosition | undefined {
    return this._seats.get(seat)?.potPosition;
  }

  /**
   * Get table pot position.
   */
  getTablePotPosition(): TargetPosition | undefined {
    return this._tablePot ?? undefined;
  }

  /**
   * Get particle layer.
   */
  getParticleLayer(): Container | undefined {
    return this._particleLayer ?? undefined;
  }

  /**
   * Get table center position.
   */
  getTableCenter(): TargetPosition | undefined {
    return this._tableCenter ?? undefined;
  }

  /**
   * Get community cards view for flip animations.
   */
  getCommunityCardsView(): CommunityCardsView | undefined {
    return this._communityCardsView ?? undefined;
  }

  /**
   * Get all registered seats.
   */
  getAllSeats(): Map<number, SeatRegistration> {
    return new Map(this._seats);
  }

  /**
   * Check if a seat is registered.
   */
  hasSeat(seat: number): boolean {
    return this._seats.has(seat);
  }

  /**
   * Get count of registered seats.
   */
  get seatCount(): number {
    return this._seats.size;
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global target registry instance.
 */
export const targetRegistry = TargetRegistry.getInstance();
